import { css, html, nothing, TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store, { waitForUser } from '../../../store/store';
import { loading } from '../../../common/constants/api-constants';
import { getFa4AppUrl, getItemLaunchObjectUrl } from '../../../common/constants/professional-redirects';
import environment from '../../../common/environment';
import emit from '../../../common/events';
import {
  appendDurationForTunnelPurchaseFlow,
  appendedDurationForHEProducts,
  getImageSrcUrl,
  isSharpenPlus,
} from '../../../common/utils/product-util';
import { formatDateDefault, formattedAuthors, ordinalSuffixOf, PROFESSIONAL } from '../../../common/utils/util';
import { StoreState } from '../../../store/store.interface';
import { loadCustomer } from '../../customer/customer';
import { isInactiveFreeTrial } from '../../free-trial/utils/free-trial.util';
import {
  LineItemStatus,
  OrderHistory as IOrderHistory,
  ProductType,
  UserOrderDetails,
} from '../../order-history/order-history.interface';
import { getOrderHistoryData } from '../../order-history/order-history.selectors';
import { fetchOrderHistory } from '../../order-history/order-history.thunks';
import { ItemType } from '../../product-card/product.interface';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { getAnalyticsSafeData, selectUser } from '../../user-profile/user-profile.selectors';
import { fetchUserProfile } from '../../user-profile/user-profile.thunks';
import { getAppFlow, getUserActiveTab } from '../../user-profile/utils/user-profile.util';
import { BaseComponent } from '../../base-component/base-component';
import { getProductTypeRedirectLink, PRODUCT_TYPE } from '../my-account-access-link';
import { getCartItemFormat } from '../../../common/utils/cart-util';
import { checkForRedirectToLogin } from '../../../common/utils/login-utils';
import { getOrderTypeFromOrderDetails, getProductTypeBU, getSharpenAnalyticsOrderDetails } from '../../../common/utils/analytics-util';
import { boardsBeyondCTA } from '../../firstaid-forward/utils/boards-beyond.util';
import {
  getLaunchSharpenUrl,
  getSharpenBundleLaunchURL,
  getSharpenCompanionTitle,
  isSharpenBundleOrder,
  isSharpenUpsellPresentInOrder,
} from '../../sharpen/utils/sharpen.util';
import { SHARPEN_PROGRAM_ID } from '../../upsell/constants/sharpen-page-type.constants';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';
import { isMhpEbook } from '../../mhp/utils/mhp.util';
import { OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { getClinicalReasoningLaunchCta } from '../../firstaid-forward/utils/clinical-reasoning.util';
import { getAccessType, renderAccessMessage } from '../../../common/utils/access-message-util';
import { expirationDate, isExpired } from './utils/my-digital-product.util';
import { getMyBookshelfLaunchCta } from '../../my-bookshelf/utils/my-bookshelf.util';

const eBookPurchaseBranding = '30DTSEBKP';
const eBookRentalBranding = '30DTSEBK';
const productSourceCreate = 'CREATE';
const HE_SEGMENT = 'HE';

@customElement('mhecomm-my-digital-products')
export class MyDigitalProducts extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .loading-container {
        display: grid;
        grid-template-columns: 2fr 8fr 2fr;
        gap: 10px;
      }
    `,
  ];

  @state()
  protected user: UserProfile | undefined;

  @state()
  protected digitalOrdersHistory: IOrderHistory[] | undefined;

  @state()
  protected loading = true;

  private _analyticsEmitted = false;

  async connectedCallback() {
    super.connectedCallback();
    store.dispatch(fetchUserProfile());
    store.dispatch(loadCustomer());
    waitForUser().then(() => {
      this.fetchOrders();
    });
  }

  protected fetchOrders(forceUpdate = false) {
    store.dispatch(fetchOrderHistory({ currentPage: '1', ...(forceUpdate && { forceUpdate: true }) }));
  }

  protected loadOrderHistory() {
    this._analyticsEmitted = false;
    this.fetchOrders(true);
  }

  protected getOrdersHistoryData(state: StoreState) {
    return getOrderHistoryData(state);
  }

  stateChanged(state: StoreState) {
    checkForRedirectToLogin(state.userProfileState.userProfileResponse);
    this.user = selectUser(state);

    const orderHistroyData = this.getOrdersHistoryData(state);

    if (orderHistroyData) {
      this.digitalOrdersHistory = this._denormalizeOrderHistory(orderHistroyData);
      this.loading = false;
      if (!this._analyticsEmitted) {
        this._analyticsEmitted = true;
        this._orderHistoryAnalyticsData(this.digitalOrdersHistory);
      }
    } else {
      this.loading = true;
    }
  }

  renderComp() {
    if (this._isLoading()) {
      return this._renderLoadingTemplate();
    } else if (this.digitalOrdersHistory) {
      return this.renderContent();
    } else {
      return html`<p>Please sign in to see your order history</p>`;
    }
  }

  renderContent() {
    const totalOrders = this.digitalOrdersHistory!.length;
    let eBookshelfLink = null;
    if (this.user) {
      eBookshelfLink = getProductTypeRedirectLink(this.user, PRODUCT_TYPE.E_BOOK);
    }

    return totalOrders === 0
      ? html`<p class="fs-3">
          You haven't placed any orders yet with this account
        </p>`
      : html`
          <div class="row justify-content-sm-center">
            <div class="col-12">
              ${eBookshelfLink
                ? html`<a
                    href="${this.user ? getProductTypeRedirectLink(this.user, PRODUCT_TYPE.E_BOOK) : ''}"
                    class="btn btn-outline-primary w-100 w-md-auto mb-4"
                    @click=${(event: Event) => this.gotoMyEbookshelf(event)}
                    aria-label="${ariaLabelWithNewTab('Go to My eBookshelf')}"
                    target="_blank"
                  >
                    Go to
                    <strong>My eBookshelf ${this.uxtIconReady ? html`<uxt-icon icon="arrow-right"></uxt-icon>` : ''}</strong>
                  </a>`
                : nothing}
              <ul class="list-group">
                ${this.digitalOrdersHistory?.flatMap(
                  (order) => order.userOrderDetails.map(
                    (item) => OfferTypeChecker.isCreateCustom(item.offerType)
                        ? html`
                          <li class="d-block">
                            <mhecomm-create-custom-order-item-display
                              .cta="${this._triggerCTAAnalyticsData.bind(this)}"
                              .item="${{...item, productDescription: this.getTitle(item)}}"
                              .order="${order}"
                              ?isMyDigitalProduct="${true}"
                            >
                            </mhecomm-create-custom-order-item-display>
                          </li>
                        `
                        : html`
                        <li class="d-block">
                          <div class="row p-3">
                            <div class="col-4 col-sm-3 col-md-2">
                              <img
                                alt=""
                                class="img-fluid"
                                src=${getImageSrcUrl(item.coverImage ? item.coverImage : item.productIsbn)}
                              />
                            </div>
                            <div class="col-8 col-sm-9 col-md-7">
                              <h2 class="h4 fs-large fw-semibold">
                                <span data-automation-id="item-title-${order.webOrderNo}-${item.isbn10}"
                                  >${this.getTitle(item)}</span
                                >
                              </h2>
                              ${item.freeTrialInfo?.isFreeTrial
                                ? html` <mhecomm-free-trial-end-date .freeTrial="${item.freeTrialInfo}" /> `
                                : nothing}
                              <div class="d-none d-sm-block">
                                <span class="fw-bold">${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span>
                                ${item.productSegment === 'SEG' ? item.mhid : item.isbn10}
                                ${item.isbn13
                                  ? html`
                                      |
                                      <span class="fw-bold">ISBN13:</span>
                                      ${item.isbn13}
                                    `
                                  : ''}
                              </div>
                              <div class="d-sm-none">
                                <span class="fw-bold">${item.productSegment === 'SEG' ? 'MHID:' : 'ISBN10:'}</span>
                                ${item.productSegment === 'SEG' ? item.mhid : item.isbn10} ${item.isbn13 ? '|' : ''}
                              </div>
                              ${item.isbn13
                                ? html`
                                    <div class="d-sm-none">
                                      <span class="fw-bold">
                                        ISBN13:
                                      </span>
                                      ${item.isbn13}
                                    </div>
                                  `
                                : ''}
                              ${item.authors && !item.freeTrialInfo?.isFreeTrial
                                ? html` <div>
                                    <span class="fw-bold">By:</span>
                                    ${formattedAuthors(item.authors)}
                                  </div>`
                                : ''}
                              ${item.grades
                                ? html` <div>
                                    <span class="fw-bold">Grades:</span>
                                    ${item.grades}
                                  </div>`
                                : ''}
                              ${item.formatDescription
                                ? html`
                                    <div>
                                      <span class="fw-bold">Format:</span>
                                      ${getCartItemFormat(item.formatDescription, item.formatCode, item.virtual)}
                                    </div>
                                  `
                                : ''}
                              ${item.licenseEndDate || item.terms
                                ? html` <div class="mt-2">
                                    ${isExpired(order.orderedDate, item.terms, item.licenseEndDate)
                                      ? html`<span class="badge rounded-pill bg-danger text-wrap">Expired</span>`
                                      : html`<span class="badge rounded-pill text-dark text-wrap bg-info"
                                          >Expires on
                                          ${formatDateDefault(
                                            item.licenseEndDate ?? expirationDate(order.orderedDate, item.terms)
                                          )}</span
                                        >`}
                                  </div>`
                                : ''}
                            </div>
                            ${item.terms && isExpired(order.orderedDate, item.terms, item.licenseEndDate)
                              ? nothing
                              : this.renderActionColumn(order, item)}
                          </div>
                          <hr class="hr-primary m-0" aria-hidden="true"/>
                        </li>
                      `
                    )
                  )}
              </ul>
            </div>
          </div>
        `;
  }

  /**
   * Renders the action column for the digital product.
   *
   * If the product has an access type (like MH Go or Virtual Labs), it shows the access message.
   *
   * Otherwise, it shows the appropriate action button based on the product details.
   *
   * @param order
   * @param item
   */
  private renderActionColumn(order: IOrderHistory, item: UserOrderDetails): TemplateResult {
    const accessType = getAccessType(item);

    if (accessType) {
      return html`
        <div class="col-12 col-md-3 text-md-end px-2 px-md-0 px-xl-2">
          <hr class="hr-primary d-md-none" aria-hidden="true"/>
          ${renderAccessMessage(accessType)}
        </div>
      `;
    }

    return html`
      <div class="col-12 col-md-3 my-auto text-md-end px-2 px-md-0 px-xl-2">
        <hr class="hr-primary d-md-none" aria-hidden="true"/>
        ${this._getButtonFromUserOrderDetails(order, item)}
        ${this.launchSharpen(order, item)}
        <mhecomm-free-trial-cancel
          addClass="mt-2"
          .product="${item}"
          orderNumber="${order.webOrderNo}"
          @mhecomm-free-trial-canceled="${() => this.loadOrderHistory()}"
        />
      </div>
    `;
  }

  gotoMyEbookshelf(event: Event) {
    const link = event.currentTarget as HTMLAnchorElement;
    emit({
      element: this,
      name: 'click',
      detail: {
        id: 'ebookshelf-button',
        label: 'My eBookshelf',
        comp: 'button',
        user: getAnalyticsSafeData(this.user!),
        appFlow: getAppFlow(),
        href: link.href,
      },
      dataLayer: true,
    });
  }

  getTitle(item: UserOrderDetails) {
    return `
      ${item.productDescription}
      ${
        item.edition && parseInt(item.edition) > 0 && !item.isEvergreen
          ? `, ${ordinalSuffixOf(item.edition)} Edition`
          : ''
      }
      ${appendedDurationForHEProducts({
        businessUnit: item?.businessUnit!,
        isVirtual: item.virtual,
        formatTypeDesc: item?.formatTypeDescription!,
        duration: item?.duration!,
        durationPeriod: item?.durationPeriod!,
        ...(item?.offerType && { offerType: item?.offerType }),
      })}
      ${item.isConnectSharpenBundle ? ' (Connect with Sharpen)' : ''}
      ${getSharpenCompanionTitle(item)}
      ${!item.duration && !item.durationPeriod && item.terms
        ? appendDurationForTunnelPurchaseFlow({
            term: item.terms,
          })
        : ''
      }
    `;
  }

  _renderLoadingTemplate() {
    return html`
      <div class="loading-container mb-4">
        ${loading({ width: '100%', height: '12rem' })}
        <div>
          ${loading({ height: '1.25rem', width: '70%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '40%' })} ${loading({ height: '1.25rem', width: '20%' })}
        </div>
        <div>
          ${loading({ height: '1.25rem', width: '100%' })} ${loading({ height: '1.25rem', width: '80%' })}
          ${loading({ height: '1.25rem', width: '50%' })}
        </div>
      </div>
    `;
  }

  _getButtonFromUserOrderDetails(order: IOrderHistory, userOrderDetails: UserOrderDetails) {
    if (userOrderDetails.freeTrialInfo && isInactiveFreeTrial(userOrderDetails.freeTrialInfo)) {
      return nothing;
    }

    if (OfferTypeChecker.isBoardsBeyond(userOrderDetails.offerType)) {
      return boardsBeyondCTA(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (OfferTypeChecker.isClinicalReasoning(userOrderDetails.offerType)) {
      return getClinicalReasoningLaunchCta(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (isMhpEbook(userOrderDetails)) {
      return html`
        <mhecomm-mhp-ebook-download
          isbn="${userOrderDetails.isbn10}"
          .eBookUrls="${userOrderDetails.extension_attributes?.ebooks_urls ?? []}"
          instructionsContainerClass="text-md-start"
        ></mhecomm-mhp-ebook-download>
      `;
    }

    if (this._isStandAloneTunnelFlow(userOrderDetails)) {
      const btnName = 'Access Now';
      const hrefGenerated = getItemLaunchObjectUrl(userOrderDetails);
      return html` <a
        class="btn btn-outline-primary w-100 w-lg-75"
        aria-label="${ariaLabelWithNewTab('Tunnel purchase')}"
        target="_blank"
        data-automation-id="btn-accessnow-${order.webOrderNo}-${userOrderDetails.isbn10}"
        @click=${() =>
          this._triggerCTAAnalyticsData('go-to-tunnel-flow', btnName, order, userOrderDetails, hrefGenerated)}
        href=${hrefGenerated}
      >
        ${btnName}&nbsp;
        <uxt-icon icon="box-arrow-up-right"></uxt-icon>
      </a>`;
    }

    if (this._isTrackOrder(userOrderDetails)) {
      const btnName = 'Track order';
      const hrefGenerated = `${environment().customerServiceUrl}/order-status`;
      return html`
        <a
          class="btn btn-outline-primary w-100 w-lg-75"
          aria-label="${ariaLabelWithNewTab('Track Order')}"
          @click=${() => this._triggerCTAAnalyticsData('track-order', btnName, order, userOrderDetails, hrefGenerated)}
          href=${hrefGenerated}
          target="_blank"
          data-automation-id="btn-trackorder-${order.webOrderNo}-${userOrderDetails.isbn10}"
        >
          ${btnName}&nbsp;
          <uxt-icon icon="box-arrow-up-right"></uxt-icon>
        </a>
      `;
    }

    if (this._isBookshelf(userOrderDetails)) {
      return getMyBookshelfLaunchCta(order, userOrderDetails, this._triggerCTAAnalyticsData.bind(this));
    }

    if (this._isLaunchButton(userOrderDetails)) {
      if (isSharpenPlus(userOrderDetails.programSourceId)) {
        return html`
          <div class="d-flex justify-content-end">
            <div class="col-12 col-lg-9 launch-button">
              <button
                type="button"
                class="btn btn-outline-primary mt-4 mb-2 w-100"
                @click=${() => this.gotosharpen(order.userOrderDetails)}
                aria-label="${ariaLabelWithNewTab('Launch Sharpen')}"
                id="gotosharpen"
                data-automation-id="btn-launch-sharpen-${order.webOrderNo}-${userOrderDetails.isbn10}"
              >
                Launch Sharpen
                <uxt-icon icon="box-arrow-up-right" class="ms-1"></uxt-icon>
              </button>
            </div>
          </div>
        `;
      } else {
        const isSharpenBundle = isSharpenBundleOrder(userOrderDetails);
        const isSharpenUpsellAvailable = isSharpenUpsellPresentInOrder(order.userOrderDetails);
        const buttonStyle = isSharpenBundle || isSharpenUpsellAvailable ? 'btn-outline-primary w-100' : 'btn-primary w-100';
        const btnName = isSharpenBundle || isSharpenUpsellAvailable ? 'Launch Connect' : 'Launch';
        return html` <div class="d-flex justify-content-end">
          <div class="col-12 col-lg-9">
            <mhecomm-connect-launch-modal
              launchLabel=${btnName}
              buttonstyle=${buttonStyle}
              icon="box-arrow-up-right"
              isbn=${userOrderDetails.isbn10}
              orderId=${order.webOrderNo}
              .connectLaunchUrl=${userOrderDetails.launchObject?.url}
              @opened-modal=${() => this._triggerCTAAnalyticsData('launch-connect', btnName, order, userOrderDetails)}
            >
            </mhecomm-connect-launch-modal>
          </div>
        </div>`;
      }
    }

    if (this._isFirstAidForward(userOrderDetails)) {
      const btnName = 'Launch';
      const hrefGenerated = getFa4AppUrl(userOrderDetails.programSourceId!);
      return html` <a
        class="btn btn-outline-primary w-100 w-lg-75"
        aria-label="${ariaLabelWithNewTab('First Aid Forward App')}"
        target="_blank"
        data-automation-id="btn-launch-${order.webOrderNo}-${userOrderDetails.isbn10}"
        @click=${() =>
          this._triggerCTAAnalyticsData('go-to-first-aid-forward', btnName, order, userOrderDetails, hrefGenerated)}
        href=${hrefGenerated}
      >
        ${btnName}&nbsp;
        <uxt-icon icon="box-arrow-up-right"></uxt-icon>
      </a>`;
    }

    if (this._isAccessCode(userOrderDetails)) {
      return html` <div class="d-flex d-md-block">
        <div class="fw-bold">
          Your Access Code:&nbsp;
        </div>
        <div class="fw-bold">
          ${userOrderDetails.accessCode}
        </div>
      </div>`;
    }

    return '';
  }

  launchSharpen(order: any, userOrderDetails) {
    if (!isSharpenBundleOrder(userOrderDetails)) {
      return nothing;
    }

    const hrefGenerated = getSharpenBundleLaunchURL(userOrderDetails);

    return html`
      <a
        class="btn btn-outline-primary w-100 w-lg-75 mt-2"
        aria-label="${ariaLabelWithNewTab('Launch Sharpen')}"
        @click=${() =>
          this._triggerCTAAnalyticsData('go-to-sharpen', 'Launch Sharpen', order, userOrderDetails, hrefGenerated)}
        href=${hrefGenerated}
        target="_blank"
        data-automation-id="btn-launch-sharpen-${order.webOrderNo}-${userOrderDetails.isbn10}"
      >
        Launch Sharpen&nbsp;
        <uxt-icon icon="box-arrow-up-right"></uxt-icon>
      </a>
    `;
  }

  _isTrackOrder(orderDetail: UserOrderDetails) {
    return orderDetail.productType === ProductType.SHIPPABLE;
  }

  _isHE(orderDetail: UserOrderDetails) {
    const segment = orderDetail.productSegment;
    return segment && segment.toUpperCase() === HE_SEGMENT;
  }

  _isEbook(orderDetail: UserOrderDetails): boolean {
    const branding = orderDetail.productBrandingCode;
    return branding === eBookRentalBranding || branding === eBookPurchaseBranding;
  }

  _isBookshelf(orderDetail: UserOrderDetails) {
    return (
      this._isHE(orderDetail) &&
      orderDetail.virtual &&
      (orderDetail.source === productSourceCreate || this._isEbook(orderDetail))
    );
  }

  _isLaunchButton(orderDetail: UserOrderDetails) {
    return (
      this._isHE(orderDetail) &&
      orderDetail.virtual &&
      !orderDetail.displayCode &&
      productSourceCreate !== orderDetail.source &&
      !this._isEbook(orderDetail)
    );
  }

  _isFirstAidForward(orderDetail: UserOrderDetails) {
    return orderDetail.productSegment === PROFESSIONAL && OfferTypeChecker.isFirstAidForward(orderDetail.offerType);
  }

  _isStandAloneTunnelFlow(orderDetail: UserOrderDetails) {
    switch (orderDetail.itemType) {
      case ItemType.ltia:
      case ItemType.apr:
      case ItemType.nutricalc:
      case ItemType.simnet:
        return true;
      default:
        return false;
    }
  }

  _isAccessCode(orderDetail: UserOrderDetails) {
    return orderDetail.virtual && orderDetail.displayCode;
  }

  _triggerCTAAnalyticsData(name: string, label: string, order: IOrderHistory, item: UserOrderDetails, href?: string) {
    emit({
      element: this,
      name,
      detail: JSON.parse(
        JSON.stringify({
          orderNumber: order.webOrderNo,
          userRole: this.user?.persona.role,
          userSegment: this.user?.persona.segment,
          orderStatus: order.orderStatusText,
          productStatus: this._getUiStatus(item),
          ...getOrderTypeFromOrderDetails(order),
          buttonName: label,
          isbn13: item.isbn13,
          mhid: item.mhid,
          itemType: item.itemType,
          editionOrRelease: item.editionOrRelease,
          ...(href && { href: href }),
          ...(getUserActiveTab() !== '' && { myAccountSection: getUserActiveTab() }),
        })
      ),
    });
  }



  _denormalizeOrderHistory(orders: IOrderHistory[]): IOrderHistory[] {
    return orders.reduce((acc: IOrderHistory[], order) => {
      if (!order.webOrderNo.startsWith('CODE_') && order.orderType?.toUpperCase() !== 'INSTRUCTOR_COMP') {
        const { ...newOrder } = order;

        newOrder.userOrderDetails = order.userOrderDetails.filter((o) => o.virtual);
        if (newOrder.userOrderDetails.length) {
          acc.push(newOrder);
        }
      }
      return acc;
    }, []);
  }

  _isLoading() {
    if (window.Mhecomm_Editor) {
      return false;
    }

    return this.loading;
  }

  _getUiStatus(orderDetail: UserOrderDetails): string {
    if (this._isHE(orderDetail) && orderDetail.virtual) {
      switch (orderDetail.lineItemStatus) {
        case LineItemStatus.COMPLETE:
          return 'Completed';
        case LineItemStatus.CANCELLED:
          return 'Cancelled';
        default:
          return 'In Progress';
      }
    }

    switch (orderDetail.lineItemStatus) {
      case LineItemStatus.SHIPPED:
        return 'Shipped';
      case LineItemStatus.PROCESSING:
      case LineItemStatus.SUBMITTED:
        return 'Ordered';
      case LineItemStatus.APPROVED:
        return 'Approved';
      case LineItemStatus.CANCELLED:
      case LineItemStatus.DENIED:
        return 'Cancelled';
      case LineItemStatus.BACKORDERED:
        return 'Backordered';
      case LineItemStatus.COMPLETE:
        return 'Completed';
      default:
        return 'In Progress';
    }
  }

  async _orderHistoryAnalyticsData(orders: IOrderHistory[]) {
    emit({
      element: this,
      name: 'digital-products-load',
      detail: {
        appFlow: getAppFlow(),
        user: getAnalyticsSafeData(this.user!, true),
        orders: orders.map((order) => this._getSafeOrderData(order)),
        totalOrders: orders.length,
        myAccountSection: getUserActiveTab(),
      },
    });
  }

  _getSafeOrderData(order: IOrderHistory) {
    return {
      webOrderNo: order.webOrderNo,
      totalPrice: order.totalPrice,
      subTotal: order.subTotal,
      orderedDate: order.orderedDate.substring(0, 10),
      isEdoms: !!order.metaOfferId,
      overAllOrderStatus: order.overAllOrderStatus.toString(),
      paymentType: order.paymentType,
      totalProductCount: order.totalProductCount,
      ...getOrderTypeFromOrderDetails(order),
      ...getProductTypeBU({ programIds: order.userOrderDetails.map((product) => product.programSourceId ?? '') }),
      ...(order.userOrderDetails.some((product) => product.programSourceId === SHARPEN_PROGRAM_ID) &&
        getSharpenAnalyticsOrderDetails(order)),
      ...(order.userOrderDetails.some((product) => product?.extension_attributes?.business_unit === PROFESSIONAL) && {
        businessUnit: PROFESSIONAL,
      }),
    };
  }

  public gotosharpen(userOrderDetailsList: UserOrderDetails[]) {
    emit({
      element: this,
      name: 'mhecomm-go-to-sharpen',
      dataLayer: true,
      detail: { id: this.id },
    });
    window.open(getLaunchSharpenUrl(userOrderDetailsList));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-digital-products': MyDigitalProducts;
  }
}
